import {
  PERMISSION_SCOPES,
  PERMISSION_CODES,
  NESTED_VIEW_PERMISSION_PAIRS,
} from '../constants/permissions';
import { isValueInList } from './helpers';

export const prepareAccessControl = accessControl => {
  const { permissions, directReports, indirectReports, customEmployeesScope } =
    accessControl ?? {};

  return {
    permissions: permissions.reduce((acc, permission) => {
      acc[permission.code] = permission;
      return acc;
    }, {}),
    directReportsSet: new Set(directReports ?? []),
    indirectReportsSet: new Set(indirectReports ?? []),
    customEmployeesScopeSet: new Set(customEmployeesScope ?? []),
  };
};

const SUCCESSION_GROUP = 'succession';
const JOB_TITLES_GROUP = 'job_titles';

const successionPermissionGroup = () => ({
  group: SUCCESSION_GROUP,
  permissions: [{ code: PERMISSION_CODES.SUCCESSION_MANAGE }],
});

const placeSuccessionGroup = permissionsGroups => {
  const groups = permissionsGroups || [];
  const successionGroup = successionPermissionGroup();
  const withoutSuccession = groups.filter(
    group => group.group !== SUCCESSION_GROUP
  );
  const jobTitlesIndex = withoutSuccession.findIndex(
    group => group.group === JOB_TITLES_GROUP
  );
  const insertAt =
    jobTitlesIndex === -1 ? withoutSuccession.length : jobTitlesIndex + 1;

  return [
    ...withoutSuccession.slice(0, insertAt),
    successionGroup,
    ...withoutSuccession.slice(insertAt),
  ];
};

export const preparePermissionsGroups = (labels, permissionsGroups) => {
  return placeSuccessionGroup(permissionsGroups).map(permissionsGroup => ({
    ...permissionsGroup,
    label: labels[permissionsGroup.group].name,
    permissions: permissionsGroup.permissions.map(permission => {
      const segments = permission.code.split('.');
      const action = segments.length === 2 ? segments.at(-1) : '';

      return {
        ...permission,
        label: labels[permissionsGroup.group].permissions[permission.code],
        tooltip:
          labels[permissionsGroup.group].tooltips?.[permission.code] ?? '',
        action,
      };
    }),
  }));
};

export const getNestedViewPairByViewCode = permissionCode =>
  NESTED_VIEW_PERMISSION_PAIRS.find(pair => pair.view === permissionCode) ??
  null;

export const getNestedViewPairsForPermission = permissionCode =>
  NESTED_VIEW_PERMISSION_PAIRS.filter(
    pair =>
      permissionCode.startsWith(pair.prefix) && permissionCode !== pair.view
  );

export const hasAuthPermissionCheck = (requiredPermission, accessControl) => {
  const permissions = accessControl?.permissions ?? {};

  return !!permissions?.[requiredPermission];
};

export const hasAuthPermissionsCheck = (requiredPermissions, accessControl) => {
  const permissions = accessControl?.permissions ?? {};

  return (requiredPermissions ?? []).map(
    permission => !!permissions?.[permission]
  );
};

export const hasScopedAuthPermissionCheck = (
  requiredPermission,
  accessControl,
  currentUserId,
  userId
) => {
  const { GLOBAL, SELF, DR, IDR, CUSTOM } = PERMISSION_SCOPES;

  const {
    permissions,
    directReportsSet,
    indirectReportsSet,
    customEmployeesScopeSet,
  } = accessControl ?? {};

  const permission = permissions?.[requiredPermission];
  if (!permission) return false;

  const scopes = permission.scopes ?? [];

  if (isValueInList(scopes, GLOBAL)) return true;

  if (userId === currentUserId) return isValueInList(scopes, SELF);

  if (isValueInList(scopes, DR)) {
    if (directReportsSet.has(userId)) return true;
  }

  if (isValueInList(scopes, IDR)) {
    if (indirectReportsSet.has(userId)) return true;
  }

  if (isValueInList(scopes, CUSTOM)) {
    if (customEmployeesScopeSet.has(userId)) return true;
  }

  return false;
};

export const hasRequiredScopeCheck = (
  requiredPermission,
  requiredScope,
  accessControl
) => {
  const permission = accessControl?.permissions?.[requiredPermission];
  if (!permission) return false;

  const scopes = permission.scopes ?? [];

  return isValueInList(scopes, requiredScope);
};

export const hasOnlySelfScopePermissionCheck = (
  requiredPermission,
  accessControl
) => {
  const permission = accessControl?.permissions?.[requiredPermission];
  if (!permission) return false;

  const scopes = permission.scopes ?? [];
  return scopes.length === 1 && isValueInList(scopes, PERMISSION_SCOPES.SELF);
};

export const isUserProfileAccessibleCheck = (
  accessControl,
  currentUserId,
  userId
) =>
  hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PROFILES_VIEW,
    accessControl,
    currentUserId,
    userId
  );

export const prepareUsersWithGrantedPermission = (
  users,
  requiredPermission,
  accesControl,
  currentUserId
) => {
  return users.reduce((acc, user) => {
    if (
      hasScopedAuthPermissionCheck(
        requiredPermission,
        accesControl,
        currentUserId,
        user.id
      )
    ) {
      return [...acc, { ...user }];
    }

    return acc;
  }, []);
};

export const prepareOrgSettingsDefaultPath = accessControl => {
  if (
    hasAuthPermissionCheck(
      PERMISSION_CODES.SETTINGS_FEATURES_MANAGE,
      accessControl
    )
  ) {
    return 'features';
  }
  if (
    hasAuthPermissionCheck(
      PERMISSION_CODES.SETTINGS_PERMISSIONS_MANAGE,
      accessControl
    )
  ) {
    return 'permissions';
  }

  if (
    hasAuthPermissionCheck(
      PERMISSION_CODES.SETTINGS_PERSON_FIELDS_INFO_MANAGE,
      accessControl
    )
  ) {
    return 'profile-info';
  }

  return 'documents';
};
